// "To review" list: tiles, filters, and a grouped expandable list.
// The open row's body is the existing stage card from queue.ts.
// Bank text and card titles are untrusted: h() text nodes only.

import { clear, h } from "../dom.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import {
  MONTH_CLOSED_CHIP,
  SETUP_FIX_CHIP,
  cardPresentation,
  queueAmountDecimal,
  type CardChipTone,
} from "../ui/card-presenter.js";
import {
  chip,
  field,
  pageHeader,
  selectField,
  statTile,
  svgIcon,
} from "../ui/components.js";
import { countOrMoreText, countText } from "../ui/display-words.js";
import * as face from "../ui/face.js";
import type { CompanyDirectory } from "../ui/face.js";
import { decimalToCents, formatCents } from "../ui/money.js";
import { navLabel } from "../ui/sentences.js";
import { presenterDirectory, slugKey } from "../ui/card-presenter.js";
import type { CompanySlug, QueueItem, StaffRole } from "../types.js";

export const WAITING_PAGE_NOTE = `Questions waiting on another system are on the ${navLabel("waiting")} page.`;

export const AM_OWNER_HIDDEN_NOTE =
  "Cards sent to the owner are not shown here.";

export type ReviewBucket = "needs-you" | "waiting";

/** The one chip word for a card that is the person's own to answer. */
export const NEEDS_YOU_CHIP = "Needs you";

/**
 * The family value the To review page gives every card the console cannot
 * answer (queue.ts re-exports it as SETUP_FIX_GROUP). Defined here so the
 * list can name those cards' sub-groups without importing the page.
 */
export const SETUP_FIX_FAMILY = "setup-fixes";

/** Tile label for the setup-fix state (docs/design/rd-polish-rules.md §1). */
export const SETUP_FIX_TILE_LABEL = "Needs a setup fix";

/**
 * "Show" filter value. `all` is every row; any other value is the EXACT chip
 * word a row must carry, so the control can never say one state and filter
 * another (Y6 brief item 8b). The options are minted from the chips the rows
 * actually render, in this stable state order.
 */
export type ShowFilter = string;

export const SHOW_ALL: ShowFilter = "all";

/** "All companies" as a company key: the empty slug (never a name). */
const ALL_COMPANIES: CompanySlug = "";

/** Display order for state words; unknown words follow, first seen. */
const CHIP_ORDER: readonly string[] = [
  NEEDS_YOU_CHIP,
  SETUP_FIX_CHIP,
  MONTH_CLOSED_CHIP,
  "Waiting on the system",
  "Waiting on the owner",
];

export interface TodayListFilters {
  search: string;
  /**
   * The company filter's value: the slug is the wire here (the select's
   * `value` and the card's own `company`), never words (B1c-1).
   */
  company: CompanySlug;
  family: string;
  show: ShowFilter;
}

export interface PresentedCard {
  item: QueueItem;
  title: string;
  happened: string;
  chipText: string;
  chipTone: CardChipTone;
  family: string;
  familyLabel: string;
  groupKey: string;
  bucket: ReviewBucket;
  amountLabel: string | null;
  amountCents: bigint | null;
  dateLabel: string | null;
  ageLabel: string;
  ageValue: string;
  companyLabel: string;
  searchText: string;
}

export interface MovementGroup {
  key: string;
  name: string;
  count: number;
  totalLabel: string | null;
  cards: PresentedCard[];
}

export interface FamilyGroup {
  key: string;
  name: string;
  count: number;
  totalLabel: string | null;
  movements: MovementGroup[];
}

export interface CompanyGroup {
  key: string;
  slug: CompanySlug;
  name: string;
  count: number;
  totalLabel: string | null;
  families: FamilyGroup[];
}

/**
 * Multi-select on the list (Q-c). Selection lives in the page so a repaint
 * never loses it; this object only renders and reports intent. Group keys are
 * the same keys `groupPresentedCards` mints.
 */
export interface ReviewSelection {
  selected: ReadonlySet<number>;
  /** True while a confirm dialog is open: every selection control is locked. */
  disabled?: boolean;
  onToggleRow: (id: number) => void;
  /** Select-all-in-this-group. The page decides toggle vs add. */
  onToggleGroup: (ids: readonly number[]) => void;
}

/** One header-level action (the Accept-suggestions disclosure on a company). */
export interface GroupHeaderAction {
  label: string;
  expanded: boolean;
  onActivate: () => void;
  /** Rendered under the header only while expanded. */
  panel: HTMLElement | null;
}

export interface TodayListExtras {
  selection?: ReviewSelection;
  groupAction?: (company: CompanyGroup) => GroupHeaderAction | null;
  /**
   * Group keys in the order they were first drawn on this page. The page
   * records it once on load and passes it back on every repaint, so answering
   * a card never re-sorts the groups under the person (Y6 brief item 1).
   */
  order?: GroupOrder;
  /**
   * True when the cards are a capped slice of a longer list. A group's count is
   * then a lower bound ("500+") and its money total is dropped: the amounts are
   * signed, so the sum of a slice is not a bound on the sum of the whole (F3).
   */
  capped?: boolean;
}

const FAMILY_LABELS: Readonly<Record<string, string>> = {
  moov: "Wallet payments",
  "escrow-balance": "Escrow balance",
  "stale-publication": "Books not updated",
  statement: "Statements",
  "month-close": "Month close",
  "period-reopened": "Reopened month",
  "closed-period-write": "Closed month",
  "unmapped-account": "Account not set up",
  "missing-account-id": "Missing account",
  "receipt-match-conflict": "Receipt does not match",
  "hub-gl-push-refused": "Hub books refused",
  "hard-match-refuse": "Receipt match refused",
  "receipt-match-review": "Receipt to confirm",
  attention: "Needs a look",
  "unmatched-hub-gl": "Hub batch",
  "untracked-counterparty": "Unknown company",
  removal: "Bank removed this",
  "booking-failed": "Could not book",
  "unmatched-deposit": "Deposit to split",
  "new-vendor": "New payee",
  receipt: "Receipt",
  bank: "Bank activity",
};

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const DAY_MS = 24 * 60 * 60 * 1000;

export function reviewBucket(tone: CardChipTone): ReviewBucket {
  return tone === "needs you" ? "needs-you" : "waiting";
}

/**
 * Badge-sized count. The noun changes when only some of the cards are yours.
 * `capped` is required: the list this count came from may be a capped slice,
 * and then its length is a lower bound that says so (F3).
 */
export function reviewHeadline(total: number, needsYou: number, capped: boolean): string {
  if (total <= 0) return "";
  const many = countOrMoreText(total, !capped);
  if (needsYou === total) {
    return total === 1 && !capped ? "1 thing needs you" : `${many} things need you`;
  }
  if (needsYou <= 0) {
    return total === 1 && !capped ? "1 thing is waiting" : `${many} things are waiting`;
  }
  return `${many} things to review`;
}

export function splitReviewCounts(cards: readonly PresentedCard[]): {
  needsYou: number;
  waiting: number;
} {
  let needsYou = 0;
  let waiting = 0;
  for (const card of cards) {
    if (card.bucket === "needs-you") needsYou += 1;
    else waiting += 1;
  }
  return { needsYou, waiting };
}

export function familyPlainLabel(family: string): string {
  const known = FAMILY_LABELS[family];
  if (known !== undefined) return known;
  const words = family.split("-").filter((word) => word.length > 0);
  if (words.length === 0) return "Other";
  return words
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

export function reviewDateLabel(iso: string | undefined): string | null {
  if (iso === undefined || iso.startsWith("1970-01-01")) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return null;
  const month = Number(match[2]);
  const day = Number(match[3]);
  const name = SHORT_MONTHS[month - 1];
  if (name === undefined || !Number.isInteger(day) || day < 1 || day > 31) return null;
  return `${name} ${String(day)}`;
}

export function waitingAge(
  createdAt: string,
  now: Date,
): { value: string; label: string } {
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return { value: "—", label: "age unknown" };
  const days = Math.floor((now.getTime() - created) / DAY_MS);
  if (days <= 0) return { value: "Today", label: "waiting less than a day" };
  if (days === 1) return { value: "1 day", label: "waiting 1 day" };
  return { value: `${String(days)} days`, label: `waiting ${String(days)} days` };
}

/** When an answer was given. "Recently" is the fallback for an unreadable date. */
export function answeredWhen(resolvedAt: string | null | undefined, now: Date): string {
  if (resolvedAt === undefined || resolvedAt === null || resolvedAt === "") {
    return "Recently";
  }
  const at = new Date(resolvedAt).getTime();
  if (Number.isNaN(at)) return "Recently";
  const days = Math.floor((now.getTime() - at) / DAY_MS);
  if (days <= 0) return "Today";
  if (days === 1) return "1 day ago";
  return `${String(days)} days ago`;
}

function amountCentsFor(item: QueueItem): bigint | null {
  const raw = queueAmountDecimal(item)?.trim() ?? "";
  if (raw.length === 0) return null;
  if (item.eventType === "removed" && /^-?0+(\.0+)?$/.test(raw)) return null;
  return decimalToCents(raw);
}

export function presentQueueCard(
  item: QueueItem,
  ctx: {
    directory?: CompanyDirectory;
    companyLabels?: ReadonlyMap<string, string>;
    role: StaffRole;
  },
  now: Date,
): PresentedCard {
  const cardFace = cardPresentation(item, ctx);
  const companyLabel = face.company(item.company, presenterDirectory(ctx), "sentence");
  const cents = amountCentsFor(item);
  const dateLabel = reviewDateLabel(item.txnDate ?? item.dueDate);
  const age = waitingAge(item.createdAt, now);
  const chipText = cardFace.chip.text.length > 0 ? cardFace.chip.text : NEEDS_YOU_CHIP;
  const familyLabel = familyPlainLabel(cardFace.family);
  const searchText = [
    cardFace.title,
    cardFace.happened,
    chipText,
    familyLabel,
    companyLabel,
    item.merchant ?? "",
    item.descriptor ?? "",
    item.amount ?? "",
    dateLabel ?? "",
  ]
    .join("\n")
    .toLowerCase();
  return {
    item,
    title: cardFace.title,
    happened: cardFace.happened,
    chipText,
    chipTone: cardFace.chip.tone,
    family: cardFace.family,
    familyLabel,
    groupKey: cardFace.groupKey,
    bucket: reviewBucket(cardFace.chip.tone),
    amountLabel: cents === null ? null : formatCents(cents),
    amountCents: cents,
    dateLabel,
    ageLabel: age.label,
    ageValue: age.value,
    companyLabel,
    searchText,
  };
}

export function sumAmountLabel(cards: readonly PresentedCard[]): string | null {
  if (cards.length === 0) return null;
  let total = 0n;
  for (const card of cards) {
    if (card.amountCents === null) return null;
    total += card.amountCents;
  }
  return formatCents(total);
}

export function filterPresentedCards(
  cards: readonly PresentedCard[],
  filters: TodayListFilters,
): PresentedCard[] {
  const query = filters.search.trim().toLowerCase();
  return cards.filter((card) => {
    if (filters.company !== "" && card.item.company !== filters.company) return false;
    if (filters.family !== "" && card.family !== filters.family) return false;
    // The filter word IS the chip word: a state can never be called one thing
    // on a row and another in the control that filters it (Y6 brief item 8b).
    if (filters.show !== SHOW_ALL && card.chipText !== filters.show) return false;
    if (query !== "" && !card.searchText.includes(query)) return false;
    return true;
  });
}

/**
 * The order every group header was first drawn in. Recorded once per page
 * load; sorting by it on later repaints keeps a group where the person saw it
 * even after its first card is answered.
 */
export type GroupOrder = ReadonlyMap<string, number>;

export function companyGroupKey(slug: CompanySlug): string {
  return slugKey(slug, "company");
}

export function familyGroupKey(slug: CompanySlug, family: string): string {
  return slugKey(slug, `family|${family}`);
}

export function movementGroupKey(family: string, groupKey: string): string {
  return `movement|${family}|${groupKey}`;
}

/** The group order for this population, first-drawn first. */
export function groupOrderFor(cards: readonly PresentedCard[]): Map<string, number> {
  const order = new Map<string, number>();
  const remember = (key: string): void => {
    if (!order.has(key)) order.set(key, order.size);
  };
  for (const card of cards) {
    remember(companyGroupKey(card.item.company));
    remember(familyGroupKey(card.item.company, card.family));
    remember(movementGroupKey(card.family, card.groupKey));
  }
  return order;
}

/**
 * A word for a wallet sub-group that is not the family's own name twice. A
 * wallet card the console cannot answer keeps its wallet group key
 * (`company|moov|type|in or out`) under the Setup fixes family, so it is named
 * the same way; naming it by its first card's title put one card's sentence
 * and amount over a group of several (integration review N4).
 */
function movementName(card: PresentedCard): string {
  const parts = card.groupKey.split("|");
  const walletShaped =
    card.family === "moov" ||
    (card.family === SETUP_FIX_FAMILY && parts[1] === "moov");
  if (!walletShaped) return card.title;
  const direction = parts[parts.length - 1] ?? "";
  const type = parts[parts.length - 2] ?? "";
  if (direction === "out") return "Money out";
  if (direction === "in") return "Money in";
  return type.length > 0 ? familyPlainLabel(type) : card.familyLabel;
}

function sortByOrder<T>(
  groups: T[],
  keyOf: (group: T) => string,
  order: GroupOrder | undefined,
): void {
  if (order === undefined) return;
  const at = (group: T): number => order.get(keyOf(group)) ?? Number.MAX_SAFE_INTEGER;
  groups.sort((a, b) => at(a) - at(b));
}

/**
 * Two sub-groups of one family may share a state word ("Money out" twice when
 * a payout and a fee went out). A repeated header is noise, and a header that
 * cannot be told from its twin is worse, so the movement's type joins the word
 * only where the word alone would repeat.
 */
function disambiguateMovementNames(family: FamilyGroup): void {
  if (family.movements.length < 2) return;
  const seen = new Map<string, number>();
  for (const movement of family.movements) {
    seen.set(movement.name, (seen.get(movement.name) ?? 0) + 1);
  }
  for (const movement of family.movements) {
    if ((seen.get(movement.name) ?? 0) < 2) continue;
    // Only a wallet movement key carries a type and a direction; a bank
    // movement is named by its merchant token and must not be renamed.
    if (!movement.key.includes("|moov|")) continue;
    const parts = movement.key.split("|");
    const direction = parts[parts.length - 1] ?? "";
    const type = parts[parts.length - 2] ?? "";
    if (type.length === 0) continue;
    const suffix =
      direction === "out" ? " out" : direction === "in" ? " in" : "";
    movement.name = `${familyPlainLabel(type)}${suffix}`;
  }
}

export function groupPresentedCards(
  cards: readonly PresentedCard[],
  order?: GroupOrder,
): CompanyGroup[] {
  const companies: CompanyGroup[] = [];
  const companyIndex = new Map<string, CompanyGroup>();
  const familyIndex = new Map<string, FamilyGroup>();
  const movementIndex = new Map<string, MovementGroup>();

  for (const card of cards) {
    let company = companyIndex.get(card.item.company);
    if (company === undefined) {
      company = {
        key: companyGroupKey(card.item.company),
        slug: card.item.company,
        name: card.companyLabel,
        count: 0,
        totalLabel: null,
        families: [],
      };
      companyIndex.set(card.item.company, company);
      companies.push(company);
    }
    const familyKey = familyGroupKey(card.item.company, card.family);
    let family = familyIndex.get(familyKey);
    if (family === undefined) {
      family = {
        key: familyGroupKey(card.item.company, card.family),
        name: card.familyLabel,
        count: 0,
        totalLabel: null,
        movements: [],
      };
      familyIndex.set(familyKey, family);
      company.families.push(family);
    }
    // The movement belongs to ONE family. Keying it by groupKey alone put a
    // "Needs you" row under a "Setup fixes" header whenever two cards of one
    // company/type/direction shared a groupKey (Y5 flag D18 / Y6 item 9).
    const movementKey = `${card.family}|${card.groupKey}`;
    let movement = movementIndex.get(movementKey);
    if (movement === undefined) {
      movement = {
        key: movementGroupKey(card.family, card.groupKey),
        // A wallet-payment group can hold several amounts; naming the first
        // card's amount over a group total would be misleading. Bank groups
        // keep the vendor title (their key is the merchant token).
        name: movementName(card),
        count: 0,
        totalLabel: null,
        cards: [],
      };
      movementIndex.set(movementKey, movement);
      family.movements.push(movement);
    }
    movement.cards.push(card);
  }

  for (const company of companies) {
    for (const family of company.families) {
      disambiguateMovementNames(family);
      sortByOrder(family.movements, (movement) => movement.key, order);
    }
    sortByOrder(company.families, (family) => family.key, order);
  }
  sortByOrder(companies, (company) => company.key, order);

  for (const company of companies) {
    const companyCards: PresentedCard[] = [];
    for (const family of company.families) {
      const familyCards: PresentedCard[] = [];
      for (const movement of family.movements) {
        movement.count = movement.cards.length;
        movement.totalLabel = sumAmountLabel(movement.cards);
        familyCards.push(...movement.cards);
      }
      family.count = familyCards.length;
      family.totalLabel = sumAmountLabel(familyCards);
      companyCards.push(...familyCards);
    }
    company.count = companyCards.length;
    company.totalLabel = sumAmountLabel(companyCards);
  }
  return companies;
}

export function reviewPageHeader(): HTMLElement {
  const header = pageHeader(navLabel("queue"));
  header.classList.add("today-page-header");
  return header;
}

function reviewStatTile(label: string, value: string, onClick?: () => void): HTMLElement {
  if (onClick === undefined) return statTile(label, value);
  return statTile(label, value, undefined, onClick);
}

/** Names the scope of a count, so two bare numbers can never be confused. */
function scopedLabel(label: string, companyName: string | undefined): string {
  const name = (companyName ?? "").trim();
  return name.length === 0 ? label : `${label} for ${name}`;
}

export interface TodayStatsScope {
  /** The company the tiles are counting, when the list is filtered to one. */
  companyName?: string;
  /**
   * True when the cards are a capped slice of a longer list: each count is a
   * lower bound and says so ("500+"), like the nav badge (F3).
   */
  capped?: boolean;
}

/**
 * The tile for each state word the cards carry. Needs you and Setup fix are
 * always shown (a zero is an answer); any other state shows only when it has
 * cards. Every card carries exactly one chip word, so the counts here add up to
 * the headline, and each tile counts exactly what its filter shows (integration
 * review N3: "Month closed" cards had no tile, so 17 + 12 sat under "34").
 */
export function reviewTileChips(cards: readonly PresentedCard[]): string[] {
  const present = new Set<string>(cards.map((card) => card.chipText));
  const always = new Set<string>([NEEDS_YOU_CHIP, SETUP_FIX_CHIP]);
  const ordered = CHIP_ORDER.filter((word) => always.has(word) || present.has(word));
  const unknown = [...present].filter((word) => !CHIP_ORDER.includes(word)).sort();
  return [...ordered, ...unknown];
}

/** The `data-tile` name of a state tile. The first two names predate this. */
function tileNameFor(chipWord: string): string {
  if (chipWord === NEEDS_YOU_CHIP) return "needs-you";
  if (chipWord === SETUP_FIX_CHIP) return "waiting";
  const slug = chipWord
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `chip-${slug.length > 0 ? slug : "other"}`;
}

/** The most tiles one row holds; more wrap onto a second row. */
const MAX_TILE_COLUMNS = 5;

export function renderTodayStats(
  host: HTMLElement,
  cards: readonly PresentedCard[],
  onShow: (show: ShowFilter) => void,
  // Already worded: the page says "500+" or "—" when the read was capped or failed.
  answeredText = "—",
  onAnswered?: () => void,
  scope: TodayStatsScope = {},
): void {
  const oldest = cards[0];
  const company = scope.companyName;
  clear(host);
  const tiles: HTMLElement[] = [];
  // One name per state (Y6 brief item 8a): the tile, the chip and the group
  // header all use the decided word, and each tile counts the cards that carry
  // exactly that chip word, which is what its click filters to.
  for (const word of reviewTileChips(cards)) {
    const count = cards.filter((card) => card.chipText === word).length;
    const label = word === SETUP_FIX_CHIP ? SETUP_FIX_TILE_LABEL : word;
    const shownCount = countText(count, scope.capped !== true);
    const tile = reviewStatTile(scopedLabel(label, company), shownCount, () => {
      onShow(word);
    });
    tile.setAttribute("data-tile", tileNameFor(word));
    tile.setAttribute("data-chip", word);
    tiles.push(tile);
  }
  const answered = reviewStatTile(
    scopedLabel("Answered recently", company),
    answeredText,
    onAnswered,
  );
  answered.setAttribute("data-tile", "answered-recently");
  const age = reviewStatTile(
    "Oldest waiting",
    oldest === undefined ? "—" : oldest.ageValue,
  );
  age.setAttribute("data-tile", "oldest");
  tiles.push(answered, age);
  host.append(...tiles);
  // The stylesheet lays out four columns. A fifth tile gets its own column
  // rather than a row of its own; more than five wrap.
  if (tiles.length > 4) {
    host.style.gridTemplateColumns = `repeat(${String(Math.min(tiles.length, MAX_TILE_COLUMNS))}, minmax(0, 1fr))`;
  } else {
    host.style.removeProperty("grid-template-columns");
  }
}

/** The state words the Show control always offers, so a tile can always set it. */
const ALWAYS_OFFERED_CHIPS: readonly string[] = [
  NEEDS_YOU_CHIP,
  SETUP_FIX_CHIP,
  MONTH_CLOSED_CHIP,
  "Waiting on the system",
];

/** The state words to offer, in the one display order; unknown words last. */
export function chipChoices(cards: readonly PresentedCard[]): string[] {
  const seen = new Set<string>(ALWAYS_OFFERED_CHIPS);
  for (const card of cards) seen.add(card.chipText);
  const known = CHIP_ORDER.filter((word) => seen.has(word));
  const unknown = [...seen].filter((word) => !CHIP_ORDER.includes(word)).sort();
  return [...known, ...unknown];
}

export interface FilterChoice {
  value: string;
  label: string;
}

function choicesFromCards(cards: readonly PresentedCard[]): {
  companies: FilterChoice[];
  families: FilterChoice[];
} {
  const companies: FilterChoice[] = [];
  const families: FilterChoice[] = [];
  const seenCompanies = new Set<CompanySlug>();
  const seenFamilies = new Set<string>();
  for (const card of cards) {
    if (!seenCompanies.has(card.item.company)) {
      seenCompanies.add(card.item.company);
      companies.push({ value: card.item.company, label: card.companyLabel });
    }
    if (!seenFamilies.has(card.family)) {
      seenFamilies.add(card.family);
      families.push({ value: card.family, label: card.familyLabel });
    }
  }
  return { companies, families };
}

/**
 * `extraCompanies` and `initialCompany` serve a capped fleet list (integration
 * re-check E5): the cards on the page are then only the oldest 500 across all
 * companies, so the company filter must offer every company and not just the
 * ones that happen to appear in that slice, and the person's company stays
 * selected. Without them the control behaves exactly as before.
 */
export function mountTodayFilters(
  host: HTMLElement,
  cards: readonly PresentedCard[],
  onChange: (filters: TodayListFilters) => void,
  options: {
    extraCompanies?: readonly FilterChoice[];
    initialCompany?: CompanySlug;
    /** The filters the page was already showing, kept across a re-read. */
    initialFilters?: TodayListFilters;
  } = {},
): { setShow: (show: ShowFilter) => void } {
  clear(host);
  const state: TodayListFilters = {
    search: options.initialFilters?.search ?? "",
    company: options.initialCompany ?? options.initialFilters?.company ?? ALL_COMPANIES,
    family: options.initialFilters?.family ?? "",
    show: options.initialFilters?.show ?? SHOW_ALL,
  };
  const publish = (): void => {
    onChange({
      search: state.search,
      company: state.company,
      family: state.family,
      show: state.show,
    });
  };
  const { companies, families } = choicesFromCards(cards);
  for (const extra of options.extraCompanies ?? []) {
    if (!companies.some((choice) => choice.value === extra.value)) companies.push(extra);
  }

  const searchField = field("Search", {
    name: "review-search",
    "aria-label": "Search cards",
    placeholder: "Search",
    autocomplete: "off",
    "data-role": "today-search",
  });
  const search = searchField.querySelector("input");
  if (search instanceof HTMLInputElement) {
    search.value = state.search;
    search.addEventListener("input", () => {
      state.search = search.value;
      publish();
    });
  }

  const companyField = selectField(
    "Company",
    [{ value: "", label: "All companies" }, ...companies],
    {
      name: "review-company",
      "aria-label": "Company",
      "data-role": "today-company-filter",
    },
  );
  const companySelect = companyField.querySelector("select");
  if (companySelect instanceof HTMLSelectElement) {
    if (state.company !== "") companySelect.value = state.company;
    companySelect.addEventListener("change", () => {
      state.company = companySelect.value;
      publish();
    });
  }

  const typeField = selectField(
    "Type",
    [{ value: "", label: "All types" }, ...families],
    {
      name: "review-type",
      "aria-label": "Type",
      "data-role": "today-type-filter",
    },
  );
  const typeSelect = typeField.querySelector("select");
  if (typeSelect instanceof HTMLSelectElement) {
    typeSelect.value = state.family;
    typeSelect.addEventListener("change", () => {
      state.family = typeSelect.value;
      publish();
    });
  }

  const showField = selectField(
    "Show",
    [
      { value: SHOW_ALL, label: "All" },
      // One option per state word the rows carry, worded exactly as the chip
      // on the row (Y6 brief item 8b).
      ...chipChoices(cards).map((word) => ({ value: word, label: word })),
    ],
    {
      name: "review-show",
      "aria-label": "Show",
      "data-role": "today-show-filter",
    },
  );
  const showSelect = showField.querySelector("select");
  if (showSelect instanceof HTMLSelectElement) {
    showSelect.value = state.show;
    showSelect.addEventListener("change", () => {
      state.show = showSelect.value === "" ? SHOW_ALL : showSelect.value;
      publish();
    });
  }

  const more = h(
    "div",
    { class: "today-filter-more", "data-role": "today-filter-more", hidden: "true" },
    [companyField, typeField, showField],
  );
  const toggle = h(
    "button",
    {
      type: "button",
      class: "today-filter-toggle btn btn-ghost",
      "data-role": "today-filter-toggle",
      "data-action": "toggle-today-filters",
      "aria-expanded": "false",
      "aria-controls": "today-filter-more",
    },
    ["Filters"],
  );
  more.id = "today-filter-more";
  const setMoreOpen = (open: boolean): void => {
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) more.removeAttribute("hidden");
    else more.setAttribute("hidden", "true");
  };
  toggle.addEventListener("click", () => {
    setMoreOpen(toggle.getAttribute("aria-expanded") !== "true");
  });

  host.append(searchField, toggle, more);

  return {
    setShow: (show: ShowFilter): void => {
      state.show = show;
      if (showSelect instanceof HTMLSelectElement) showSelect.value = show;
      publish();
    },
  };
}

function chevron(open: boolean): HTMLElement {
  return h(
    "span",
    {
      class: "today-chevron",
      "data-open": open ? "true" : "false",
      "aria-hidden": "true",
    },
    [svgIcon("chevron")],
  );
}

function statusChip(card: PresentedCard): HTMLElement {
  const tone = card.chipTone === "needs you" ? "warn" : "info";
  const node = chip(tone, card.chipText);
  node.classList.add("today-row-chip");
  return node;
}

function renderRow(
  card: PresentedCard,
  expandedIds: ReadonlySet<number>,
  onToggleRow: (id: number) => void,
  renderExpanded: (item: QueueItem) => HTMLElement,
  selection?: ReviewSelection,
): HTMLElement {
  const open = expandedIds.has(card.item.id);
  const toggle = h(
    "button",
    {
      type: "button",
      class: "today-row-toggle",
      "data-role": "today-row-toggle",
      "data-action": "toggle-review-row",
      "data-queue-id": String(card.item.id),
      "aria-expanded": open ? "true" : "false",
    },
    [
      chevron(open),
      h("span", { class: "today-row-title", "data-role": "today-row-title" }, [amVisibleDisplayText(card.title)]),
      h("span", { class: "today-row-amount", "data-role": "today-row-amount" }, [
        card.amountLabel ?? "—",
      ]),
      // The date, state word and age sit in ONE group: on a phone the row
      // header keeps chevron, title and amount on the first line and this
      // group on a second, so the header stays under ~90px (Y6 brief item 4).
      h("span", { class: "today-row-meta" }, [
        h("span", { class: "today-row-date", "data-role": "today-row-date" }, [
          card.dateLabel ?? "—",
        ]),
        statusChip(card),
        h("span", { class: "today-row-age", "data-role": "today-row-age" }, [card.ageLabel]),
      ]),
    ],
  );
  toggle.addEventListener("click", () => {
    onToggleRow(card.item.id);
  });
  const line: Array<HTMLElement> = [];
  if (selection !== undefined) {
    const box = h("input", {
      type: "checkbox",
      class: "today-row-select",
      "data-role": "select-review-row",
      "data-action": "select-review-row",
      "data-queue-id": String(card.item.id),
      "aria-label": `Select ${amVisibleDisplayText(card.title)}`,
    });
    box.checked = selection.selected.has(card.item.id);
    box.disabled = selection.disabled === true;
    box.addEventListener("change", () => {
      selection.onToggleRow(card.item.id);
    });
    line.push(box);
  }
  line.push(toggle);
  const row = h(
    "article",
    {
      class: open ? "today-row today-row-open" : "today-row",
      "data-role": "today-row",
      "data-queue-id": String(card.item.id),
      "data-company": card.item.company,
      "data-family": card.family,
      "data-expanded": open ? "true" : "false",
      "data-bucket": card.bucket,
    },
    [h("div", { class: "today-row-line" }, line)],
  );
  if (open) {
    row.append(
      h("div", { class: "today-row-body", "data-role": "today-row-body" }, [
        renderExpanded(card.item),
      ]),
    );
  }
  return row;
}

function groupSelectBox(
  key: string,
  name: string,
  cards: readonly PresentedCard[],
  selection: ReviewSelection,
): HTMLElement {
  const ids = cards.map((card) => card.item.id);
  const all = ids.length > 0 && ids.every((id) => selection.selected.has(id));
  const some = ids.some((id) => selection.selected.has(id));
  const box = h("input", {
    type: "checkbox",
    class: "today-group-select",
    "data-role": "select-review-group",
    "data-action": "select-review-group",
    "data-group-key": key,
    "aria-label": `Select all in ${name}`,
  });
  box.checked = all;
  box.indeterminate = some && !all;
  box.disabled = selection.disabled === true;
  box.addEventListener("change", () => {
    selection.onToggleGroup(ids);
  });
  return box;
}

function renderGroup(
  level: "company" | "family" | "movement",
  key: string,
  name: string,
  count: number,
  total: string | null,
  collapsedGroupKeys: ReadonlySet<string>,
  onToggleGroup: (groupKey: string) => void,
  children: readonly HTMLElement[],
  extras: {
    selection?: ReviewSelection;
    cards?: readonly PresentedCard[];
    action?: GroupHeaderAction | null;
    capped?: boolean;
  } = {},
): HTMLElement {
  const open = !collapsedGroupKeys.has(key);
  const parts: Array<HTMLElement | string> = [
    chevron(open),
    h("span", { class: "today-group-name", "data-role": "today-group-name" }, [name]),
    h("span", { class: "today-group-count", "data-role": "today-group-count" }, [
      countText(count, extras.capped !== true),
    ]),
  ];
  if (total !== null && extras.capped !== true) {
    parts.push(
      h("span", { class: "today-group-total", "data-role": "today-group-total" }, [total]),
    );
  }
  const toggle = h(
    "button",
    {
      type: "button",
      class: `today-group-toggle today-group-toggle-${level}`,
      "data-role": "today-group-toggle",
      "data-action": "toggle-review-group",
      "data-group-key": key,
      "aria-expanded": open ? "true" : "false",
    },
    parts,
  );
  toggle.addEventListener("click", () => {
    onToggleGroup(key);
  });
  const head: Array<HTMLElement> = [];
  if (extras.selection !== undefined && extras.cards !== undefined) {
    head.push(groupSelectBox(key, name, extras.cards, extras.selection));
  }
  head.push(toggle);
  const action = extras.action ?? null;
  if (action !== null) {
    const actionButton = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost today-group-action",
        "data-role": "group-accept-suggestions",
        "data-action": "accept-suggestions",
        "aria-expanded": action.expanded ? "true" : "false",
      },
      [action.label],
    );
    actionButton.addEventListener("click", () => {
      action.onActivate();
    });
    head.push(actionButton);
  }
  const headRow = h("div", { class: "today-group-head" }, head);
  const childrenNodes: HTMLElement[] = [headRow];
  if (action !== null && action.expanded && action.panel !== null) {
    const panel = h(
      "div",
      { class: "today-group-panel", "data-role": "group-accept-panel" },
      [action.panel],
    );
    childrenNodes.push(panel);
  }
  const body = h(
    "div",
    { class: "today-group-body", "data-role": "today-group-body" },
    children,
  );
  if (!open) {
    body.hidden = true;
    body.setAttribute("hidden", "true");
  }
  childrenNodes.push(body);
  return h(
    "section",
    {
      class: `today-group today-group-${level}`,
      "data-role": "today-group",
      "data-level": level,
      "data-group-key": key,
      "data-open": open ? "true" : "false",
    },
    childrenNodes,
  );
}

export function renderTodayList(
  host: HTMLElement,
  cards: readonly PresentedCard[],
  filters: TodayListFilters,
  expandedIds: ReadonlySet<number>,
  collapsedGroupKeys: ReadonlySet<string>,
  onToggleRow: (id: number) => void,
  onToggleGroup: (groupKey: string) => void,
  renderExpanded: (item: QueueItem) => HTMLElement,
  extras: TodayListExtras = {},
): void {
  clear(host);
  const visible = filterPresentedCards(cards, filters);
  const list = h("div", {
    class: "today-list",
    "data-role": "today-list",
    "data-count": String(visible.length),
  });
  host.append(list);
  if (cards.length === 0) return;
  if (visible.length === 0) {
    list.append(
      h("p", { class: "today-empty", "data-role": "today-empty" }, ["Nothing matches."]),
    );
    return;
  }
  for (const company of groupPresentedCards(visible, extras.order)) {
    const familyNodes: HTMLElement[] = [];
    for (const family of company.families) {
      const movementNodes: HTMLElement[] = [];
      for (const movement of family.movements) {
        const rows = movement.cards.map((card) =>
          renderRow(card, expandedIds, onToggleRow, renderExpanded, extras.selection),
        );
        if (family.movements.length === 1) {
          // One movement in a family: the family header already carries the
          // count and total, so a second identical header is noise. A group of
          // exactly one card is likewise a plain row.
          movementNodes.push(...rows);
          continue;
        }
        // One card is a plain row, never a header plus the same card. The
        // movement key no longer includes the amount (Q-d), so a group only
        // reaches this branch when it genuinely has one card.
        movementNodes.push(
          movement.cards.length === 1
            ? (rows[0] as HTMLElement)
            : renderGroup(
                "movement",
                movement.key,
                movement.name,
                movement.count,
                movement.totalLabel,
                collapsedGroupKeys,
                onToggleGroup,
                rows,
                {
                  ...(extras.selection === undefined
                    ? {}
                    : { selection: extras.selection, cards: movement.cards }),
                  capped: extras.capped === true,
                },
              ),
        );
      }
      const familyCards = family.movements.flatMap((movement) => movement.cards);
      familyNodes.push(
        renderGroup(
          "family",
          family.key,
          family.name,
          family.count,
          family.totalLabel,
          collapsedGroupKeys,
          onToggleGroup,
          movementNodes,
          {
            ...(extras.selection === undefined
              ? {}
              : { selection: extras.selection, cards: familyCards }),
            capped: extras.capped === true,
          },
        ),
      );
    }
    const companyCards = company.families.flatMap((family) =>
      family.movements.flatMap((movement) => movement.cards),
    );
    list.append(
      renderGroup(
        "company",
        company.key,
        company.name,
        company.count,
        company.totalLabel,
        collapsedGroupKeys,
        onToggleGroup,
        familyNodes,
        {
          ...(extras.selection === undefined
            ? {}
            : { selection: extras.selection, cards: companyCards }),
          ...(extras.groupAction === undefined
            ? {}
            : { action: extras.groupAction(company) }),
          capped: extras.capped === true,
        },
      ),
    );
  }
}
